/**
 * @fileoverview Estilos do "Gerenciar Agentes" (/fluxo) que o modal de Triagens
 * não tem: a foto ou as iniciais do agente, a busca, o painel das versões, os
 * arquivos, as respostas prontas e a conversa de teste. O resto (modal,
 * cabeçalho, abas, linhas, campos e botões) vem dos estilos do modal de
 * Triagens, para os dois modais da página terem a mesma cara.
 */

import { makeStyles } from "@material-ui/core/styles";

const useAgentStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const textPrimary = isDark ? "#f0f2f5" : "#111318";
  const textSecondary = isDark ? "#a0aec0" : "#4b5563";
  const textFaint = isDark ? "#8a94a6" : "#6b7280";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.12)";
  const inputBorder = isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.2)";
  const focusBorder = isDark ? "rgba(91,124,255,0.6)" : "rgba(61,92,253,0.55)";
  const focusRing = isDark ? "0 0 0 1px rgba(91,124,255,0.15)" : "0 0 0 1px rgba(61,92,253,0.12)";
  const surface = isDark ? "#111318" : "#ffffff";
  const surfaceAlt = isDark ? "rgba(255,255,255,0.03)" : "#f8fafc";
  const avatarBg = isDark ? "rgba(255,255,255,0.06)" : "#F4F4F5";
  const userBubble = isDark ? "rgba(91,124,255,0.16)" : "rgba(61,92,253,0.08)";
  const warn = isDark ? "#fbbf24" : "#b45309";
  const t = "all 160ms cubic-bezier(0.4, 0, 0.2, 1)";

  const input = {
    height: 36,
    padding: "0 12px",
    borderRadius: 10,
    border: `1px solid ${inputBorder}`,
    background: surface,
    color: textPrimary,
    fontSize: 13.5,
    fontFamily: "inherit",
    outline: "none",
    transition: t,
    "&::placeholder": { color: textFaint },
    "&:focus": { borderColor: focusBorder, boxShadow: focusRing },
  };

  return {
    // Largo, com respiro (até ~1.120px), como os modais de configuração do app.
    paperWide: { width: "calc(100% - 64px)", maxWidth: 1120, [theme.breakpoints.down("xs")]: { width: "100%" } },
    avatar: {
      width: 36,
      height: 36,
      borderRadius: "50%",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: avatarBg,
      color: textSecondary,
      fontSize: 13,
      fontWeight: 600,
      overflow: "hidden",
      "& img": { width: "100%", height: "100%", objectFit: "cover" },
    },
    avatarSmall: { width: 28, height: 28, fontSize: 11 },
    avatarLarge: { width: 56, height: 56, fontSize: 18 },
    search: { ...input, flex: 1, minWidth: 180, maxWidth: 320 },
    toolbarButtons: { display: "flex", gap: 8, flexWrap: "wrap", marginLeft: "auto" },
    subtitle: { fontSize: 12.5, color: textSecondary, marginTop: 4, lineHeight: 1.45 },
    usedIn: { fontSize: 12.5, color: textSecondary, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    rowLead: { display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 },
    // Cabeçalho do editor: a foto e o nome
    headerAgent: { display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 },
    // Conhecimento: o texto e, ao lado, as versões
    knowledge: { display: "flex", gap: 20, alignItems: "flex-start", [theme.breakpoints.down("sm")]: { flexDirection: "column" } },
    knowledgeMain: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 10, width: "100%" },
    knowledgeHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
    promptField: {
      "& .MuiInputBase-root": { fontSize: 13.5, lineHeight: 1.55, alignItems: "flex-start" },
      "& textarea": { fontFamily: "inherit" },
    },
    versions: {
      width: 280,
      flexShrink: 0,
      borderLeft: `1px solid ${border}`,
      paddingLeft: 16,
      animation: "$slideIn 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      [theme.breakpoints.down("sm")]: { width: "100%", borderLeft: "none", paddingLeft: 0, borderTop: `1px solid ${border}`, paddingTop: 12 },
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    },
    "@keyframes slideIn": { from: { opacity: 0, transform: "translateX(8px)" }, to: { opacity: 1, transform: "none" } },
    versionsHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
    versionsTitle: { fontSize: 13, fontWeight: 600, color: textPrimary },
    versionItem: { padding: "10px 0", borderBottom: `1px solid ${border}`, display: "flex", alignItems: "center", gap: 10, "&:last-child": { borderBottom: "none" } },
    versionText: { flex: 1, minWidth: 0 },
    versionName: { fontSize: 13, fontWeight: 600, color: textPrimary },
    versionMeta: { fontSize: 11.5, color: textFaint, marginTop: 2 },
    // Fontes e Inteligência em duas colunas (uma no celular), na largura toda do modal
    columns: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "26px 36px",
      alignItems: "start",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    column: { display: "flex", flexDirection: "column", gap: 26, minWidth: 0 },
    // Arquivos e respostas prontas
    section: { display: "flex", flexDirection: "column", gap: 10 },
    list: { display: "flex", flexDirection: "column", gap: 8 },
    fileRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 12px",
      borderRadius: 10,
      border: `1px solid ${border}`,
      background: surfaceAlt,
      "& > svg": { width: 16, height: 16, color: textSecondary, flexShrink: 0 },
    },
    fileName: { flex: 1, minWidth: 0, fontSize: 13, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    fileMeta: { fontSize: 11.5, color: textFaint, flexShrink: 0 },
    caseCard: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 8, alignItems: "start", padding: 12, borderRadius: 12, border: `1px solid ${border}` },
    caseFields: { display: "flex", flexDirection: "column", gap: 10 },
    muted: { fontSize: 12.5, color: textFaint },
    storeRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: textPrimary, cursor: "pointer" },
    switchRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontSize: 13, color: textPrimary, padding: "4px 0" },
    photoRow: { display: "flex", alignItems: "center", gap: 14 },
    note: { fontSize: 12.5, color: textSecondary, lineHeight: 1.5 },
    warnNote: { fontSize: 12.5, color: warn, lineHeight: 1.5 },
    // Conversa de teste
    chat: {
      display: "flex",
      flexDirection: "column",
      height: "min(56vh, 520px)",
      minHeight: 320,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
      background: surface,
    },
    chatMessages: { flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 8, ...theme.scrollbarStylesSoft },
    chatEmpty: { margin: "auto", fontSize: 13, color: textFaint, textAlign: "center", maxWidth: 260, lineHeight: 1.5 },
    bubble: {
      maxWidth: "78%",
      padding: "9px 12px",
      borderRadius: 14,
      fontSize: 13.5,
      lineHeight: 1.45,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      color: textPrimary,
      animation: "$bubbleIn 160ms ease-out",
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    },
    "@keyframes bubbleIn": { from: { opacity: 0, transform: "translateY(4px)" }, to: { opacity: 1, transform: "none" } },
    bubbleUser: { alignSelf: "flex-end", background: userBubble, borderBottomRightRadius: 4 },
    bubbleAi: { alignSelf: "flex-start", background: surfaceAlt, border: `1px solid ${border}`, borderBottomLeftRadius: 4 },
    bubbleError: { color: warn },
    bubbleFiles: { fontSize: 11.5, color: textFaint, marginTop: 4 },
    typing: {
      alignSelf: "flex-start",
      display: "flex",
      gap: 4,
      padding: "12px 14px",
      borderRadius: 14,
      background: surfaceAlt,
      border: `1px solid ${border}`,
      "& span": {
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: textFaint,
        animation: "$blink 1.2s infinite ease-in-out",
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      },
      "& span:nth-child(2)": { animationDelay: "0.15s" },
      "& span:nth-child(3)": { animationDelay: "0.3s" },
    },
    "@keyframes blink": { "0%, 80%, 100%": { opacity: 0.3 }, "40%": { opacity: 1 } },
    chatInputRow: { display: "flex", gap: 8, padding: 10, borderTop: `1px solid ${border}` },
    chatInput: { ...input, flex: 1, minWidth: 0 },
  };
});

export default useAgentStyles;
